import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useServerAccountValidation } from '../hooks/useServerAccountValidation';
import { useUpsertAccountForm } from '../hooks/useUpsertAccountForm';
import type { AccountDto, UpsertAccountDto } from '../types/AccountDto';
import { UpsertAccountForm } from './UpsertAccountForm';

interface EditAccountModalProps {
	close: () => void;
	opened: boolean;
	defaultAccount: UpsertAccountDto;
	updateTableValue: (a: AccountDto) => void;
}

export function EditAccountModal({
	opened,
	close,
	defaultAccount,
	updateTableValue,
}: EditAccountModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertAccountForm(defaultAccount);

	const editAccountMutation = mutate<UpsertAccountDto, AccountDto>(
		`${organisation}/user/edit/${inputs.id}`,
		inputs,
		'multipart/form-data',
	);

	useServerAccountValidation(editAccountMutation, form);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		if (editAccountMutation.data?.value) {
			updateTableValue(editAccountMutation.data.value);
			form.setInitialValues(inputs);
		}

		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={resetAndClose}
				title={<Title label={'Edit Account'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={editAccountMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Account Edited!'}
					body={'Account has been successfully edited. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editAccountMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertAccountForm
						form={form}
						isEdit={inputs.isEdit}
						selectedBranchIds={inputs.branchIds}
					/>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}